/**
 * What a numeric field accepts.
 *
 * One description per field — the grid it sits on, how many decimals it may
 * carry, its floor and ceiling, and which way an off-grid value is resolved —
 * so the input, the validator, the MAX button and the submit guard all read the
 * same rules instead of each deriving their own.
 */
import { GetMarkets200Response } from "../../codegen-api";
import { getContractPriceStep, IContractPriceStep } from "../instruments";
import { Dec, dec } from "./decimal";

export type FieldKind =
  | "size"
  | "price"
  | "amount"
  | "percent"
  | "leverage"
  | "slippage";

/** Which side an off-grid value is resolved to. `none` leaves it alone. */
export type Finalize = "floor" | "ceil" | "none";

export interface NumericFieldSpec {
  kind: FieldKind;
  /** The grid: a market's step, or one unit of the token's decimals. */
  step: Dec;
  /** Decimal places the field may carry; never coarser than the step. */
  maxDecimals: number;
  /** Inclusive floor (a market's minimum order size, 1x leverage). */
  min?: Dec;
  /** Inclusive ceiling (100%, max leverage, the market's max order size). */
  max?: Dec;
  finalize: Finalize;
  /** Shown beside the field: "BTC", "USDC", "%", "x". */
  unit: string;
}

/** The market's grid as exact text, straight from the API strings. */
export interface MarketSteps {
  amountStep: Dec;
  priceStep: Dec;
  minOrderAmount: Dec;
  amountPrecision: number;
  pricePrecision: number;
  /** Read from the chain; absent until `useMarketContractSpecs` resolves. */
  maxOrderAmount?: Dec;
}

/** Decimal places a step needs: "0.00000001" -> 8, "0.5" -> 1, "1" -> 0. */
export const stepDecimals = (step: Dec | number): number => {
  const text = dec.parse(step);
  if (text === null) return 0;
  return text.split(".")[1]?.length ?? 0;
};

/** A field may never be coarser than its own grid. */
const decimalsFor = (step: Dec, precision: number): number =>
  Math.min(20, Math.max(precision, stepDecimals(step)));

/**
 * The market's grid. `min_order_amount` is the chain's floor and is distinct
 * from the rounding step: BTC trades in 1e-8 increments but will not accept an
 * order below 0.00001.
 */
export const marketSteps = (
  market?: GetMarkets200Response,
  onChain?: { amountStep?: Dec; priceStep?: Dec; minOrderSize?: Dec; maxOrderSize?: Dec }
): MarketSteps => {
  const api: IContractPriceStep = getContractPriceStep(market);
  const amountStep = dec.parse(onChain?.amountStep ?? api.amount_step) ?? "0.01";
  const priceStep = dec.parse(onChain?.priceStep ?? api.price_step) ?? "0.01";
  const min =
    dec.parse(onChain?.minOrderSize ?? api.min_order_amount) ?? amountStep;
  const max = onChain?.maxOrderSize ? dec.parse(onChain.maxOrderSize) : null;
  return {
    amountStep,
    priceStep,
    // A minimum below one step cannot be met; the step is the real floor.
    minOrderAmount: dec.gt(min, amountStep) ? min : amountStep,
    amountPrecision: api.amount_precision,
    pricePrecision: api.price_precision,
    // 0 from the chain means "no cap", not "nothing may be ordered".
    maxOrderAmount: max && dec.gt(max, "0") ? max : undefined,
  };
};

/** An order size, in the base asset. */
export const sizeSpec = (steps: MarketSteps, unit: string): NumericFieldSpec => ({
  kind: "size",
  step: steps.amountStep,
  maxDecimals: decimalsFor(steps.amountStep, steps.amountPrecision),
  min: steps.minOrderAmount,
  max: steps.maxOrderAmount,
  finalize: "floor",
  unit,
});

/**
 * An order size entered in quote terms. Cents, not the market's price step:
 * rounding a USDC amount with a 0.5 price step turned $10.25 into $10.
 */
export const quoteAmountSpec = (unit = "USDC"): NumericFieldSpec => ({
  kind: "amount",
  step: "0.01",
  maxDecimals: 2,
  finalize: "floor",
  unit,
});

/**
 * A limit or trigger price. `side` decides which way an off-grid price is
 * resolved: a buy floors (never pays more than typed), a sell ceils.
 */
export const priceSpec = (
  steps: MarketSteps,
  side: "buy" | "sell",
  unit = "USDC"
): NumericFieldSpec => ({
  kind: "price",
  step: steps.priceStep,
  maxDecimals: decimalsFor(steps.priceStep, steps.pricePrecision),
  min: steps.priceStep,
  finalize: side === "sell" ? "ceil" : "floor",
  unit,
});

/** A token amount: deposits, withdrawals, transfers, collateral. */
export const tokenAmountSpec = (decimals: number, unit: string): NumericFieldSpec => {
  const step = dec.fromUnits(BigInt(1), decimals);
  return {
    kind: "amount",
    step,
    maxDecimals: decimals,
    finalize: "floor",
    unit,
  };
};

export const percentSpec = (): NumericFieldSpec => ({
  kind: "percent",
  step: "1",
  maxDecimals: 0,
  min: "0",
  max: "100",
  finalize: "floor",
  unit: "%",
});

export const leverageSpec = (maxLeverage: number): NumericFieldSpec => ({
  kind: "leverage",
  step: "1",
  maxDecimals: 0,
  min: "1",
  max: dec.parse(Math.max(1, Math.floor(maxLeverage) || 1)) ?? "1",
  finalize: "floor",
  unit: "x",
});

/**
 * Max slippage, in percent. 0.01% is the finest the bps encoding can carry.
 * `min`/`max` are the range a bound must sit in, and SlippageControl refuses a
 * value outside it with a reason rather than clamping it on blur (QA 2026-10-01).
 */
export const slippageSpec = (): NumericFieldSpec => ({
  kind: "slippage",
  step: "0.01",
  maxDecimals: 2,
  min: "0.01",
  max: "50",
  finalize: "floor",
  unit: "%",
});
